/**
 * ThemeContext
 *
 * Manages the app's appearance preference with an explicit two-way switch:
 *   'light' — force light theme
 *   'dark'  — force dark theme
 *
 * Priority:
 *   1. Saved user preference once account identity is known
 *   2. Saved global/document preference during pre-auth boot
 *   3. Falls back to 'dark'
 *
 * The selected theme is applied as data-theme="light" or data-theme="dark"
 * on document.documentElement so CSS vars in index.css pick it up globally.
 */

import React, { createContext, useContext, useLayoutEffect, useState, useCallback } from 'react';
import AuthContext from '../AuthContext';

const STORAGE_KEY = 'vylo-theme-preference';
const DEFAULT_THEME = 'dark';

const normalizeTheme = (value) => (value === 'light' || value === 'dark' ? value : null);
const normalizeEmail = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  return trimmed || null;
};

const resolveAccountIdentity = (authContext) => {
  const user = authContext?.user;
  return {
    userId: user?.id != null ? String(user.id) : null,
    email: normalizeEmail(user?.email),
  };
};

const getAccountStorageKey = (accountIdentity) => {
  if (!accountIdentity) return null;
  const { userId } = accountIdentity;
  if (userId) return `${STORAGE_KEY}:user:${userId}`;
  return null;
};

const getLegacyAccountStorageKeys = (accountIdentity) => {
  if (!accountIdentity) return [];
  const keys = [];
  if (accountIdentity.userId && accountIdentity.email) {
    keys.push(`${STORAGE_KEY}:account:${accountIdentity.userId}:${accountIdentity.email}`);
  }
  if (accountIdentity.email) keys.push(`${STORAGE_KEY}:email:${accountIdentity.email}`);
  return keys;
};

export const isForcedDarkPath = (pathname) => (
  pathname === '/mobile-connect-bank'
);

const getCurrentPathname = () => {
  if (typeof window === 'undefined') return '';
  return window.location.pathname || '';
};

export function clearStoredThemePreferenceForAccount(accountIdentity, options = {}) {
  const { includeLegacyGlobal = false } = options;
  try {
    const primaryKey = getAccountStorageKey(accountIdentity);
    const keys = new Set([
      primaryKey,
      ...getLegacyAccountStorageKeys(accountIdentity),
      includeLegacyGlobal ? STORAGE_KEY : null,
    ].filter(Boolean));
    keys.forEach((key) => localStorage.removeItem(key));
  } catch {
    // localStorage unavailable
  }
}

/** Apply the selected theme to <html data-theme="...">. */
function applyTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
}

function readDocumentPreference() {
  if (typeof document === 'undefined') return null;
  return normalizeTheme(document.documentElement.getAttribute('data-theme'));
}

/** Read the saved preference, or fall back to 'dark'. */
function readSavedPreference({
  accountIdentity = null,
  allowLegacyFallback = false,
  preferDocumentPreference = false,
} = {}) {
  try {
    if (accountIdentity) {
      const scopedKey = getAccountStorageKey(accountIdentity);
      const scoped = scopedKey ? normalizeTheme(localStorage.getItem(scopedKey)) : null;
      if (scoped) return scoped;
      const legacy = getLegacyAccountStorageKeys(accountIdentity)
        .map((key) => normalizeTheme(localStorage.getItem(key)))
        .find(Boolean);
      if (legacy) return legacy;
      return DEFAULT_THEME;
    }

    if (allowLegacyFallback) {
      const saved = normalizeTheme(localStorage.getItem(STORAGE_KEY));
      if (saved) return saved;
    }
  } catch {
    // localStorage unavailable (SSR, private mode, etc.)
  }
  if (preferDocumentPreference) {
    const documentPreference = readDocumentPreference();
    if (documentPreference) return documentPreference;
  }
  return DEFAULT_THEME;
}

function migrateLegacyPreferenceForAccount(accountIdentity) {
  const primaryKey = getAccountStorageKey(accountIdentity);
  if (!primaryKey) return null;

  try {
    const primary = normalizeTheme(localStorage.getItem(primaryKey));
    if (primary) return primary;

    const legacyKeys = getLegacyAccountStorageKeys(accountIdentity);
    const legacyPreference = legacyKeys
      .map((key) => normalizeTheme(localStorage.getItem(key)))
      .find(Boolean);
    if (!legacyPreference) return null;

    localStorage.setItem(primaryKey, legacyPreference);
    legacyKeys.forEach((key) => localStorage.removeItem(key));
    localStorage.removeItem(STORAGE_KEY);
    return legacyPreference;
  } catch {
    return null;
  }
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children, pathname = getCurrentPathname() }) {
  const authContext = useContext(AuthContext);
  const hasAuthProvider = authContext !== null;
  const accountIdentity = resolveAccountIdentity(authContext);
  const accountUserId = accountIdentity.userId;
  const accountEmail = accountIdentity.email;
  const [preference, setPreference] = useState(() => readSavedPreference({
    allowLegacyFallback: true,
    preferDocumentPreference: true,
  }));
  const forcedPreference = isForcedDarkPath(pathname) ? DEFAULT_THEME : null;
  const effectivePreference = forcedPreference || preference;

  useLayoutEffect(() => {
    const scopedAccountIdentity = accountUserId || accountEmail
      ? { userId: accountUserId, email: accountEmail }
      : null;
    const migratedPreference = scopedAccountIdentity
      ? migrateLegacyPreferenceForAccount(scopedAccountIdentity)
      : null;
    setPreference(
      migratedPreference
        ?? readSavedPreference({
          accountIdentity: scopedAccountIdentity,
          allowLegacyFallback: !scopedAccountIdentity,
          preferDocumentPreference: !scopedAccountIdentity,
        })
    );
  }, [accountEmail, accountUserId, hasAuthProvider]);

  useLayoutEffect(() => {
    applyTheme(effectivePreference);
    return () => {
      document.documentElement.removeAttribute('data-theme');
    };
  }, [effectivePreference]);

  const setThemePreference = useCallback((next) => {
    if (next !== 'light' && next !== 'dark') return;
    if (forcedPreference) {
      setPreference(DEFAULT_THEME);
      return;
    }
    const scopedAccountIdentity = accountUserId || accountEmail
      ? { userId: accountUserId, email: accountEmail }
      : null;
    try {
      const scopedKey = getAccountStorageKey(scopedAccountIdentity);
      if (scopedKey) {
        localStorage.setItem(scopedKey, next);
        getLegacyAccountStorageKeys(scopedAccountIdentity)
          .filter((key) => key !== scopedKey)
          .forEach((key) => localStorage.removeItem(key));
        localStorage.removeItem(STORAGE_KEY);
      } else if (!hasAuthProvider) {
        localStorage.setItem(STORAGE_KEY, next);
      }
    } catch {
      // ignore write failures
    }
    setPreference(next);
  }, [accountEmail, accountUserId, forcedPreference, hasAuthProvider]);

  return (
    <ThemeContext.Provider value={{ preference: effectivePreference, setThemePreference }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * @returns {{ preference: 'light'|'dark', setThemePreference: (p: string) => void }}
 */
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return {
      preference: 'dark',
      setThemePreference: () => {},
    };
  }
  return ctx;
}
